import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';

const ADMIN_API = 'https://admin.riymay.com/api/v1/admin';

const Home = () => {
  const [shelves, setShelves] = useState([]);
  const [banners, setBanners] = useState([]);
  const [heroMedia, setHeroMedia] = useState(null);
  // `settled` = the hero media request finished, either way. `armed` = this
  // component mounted, so JS is definitely running and it is safe to hold the
  // headline back. `ready` = the hero photo has actually arrived (or we gave
  // up waiting), which is the moment the entrance animation should play.
  const [heroSettled, setHeroSettled] = useState(false);
  const [heroArmed, setHeroArmed] = useState(false);
  const [heroReady, setHeroReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${ADMIN_API}/homepage/data`)
      .then(r => r.json())
      .then(d => {
        setShelves(d.shelves || []);
        setBanners(d.banners || []);
      })
      .catch(() => {});

    fetch(`${ADMIN_API}/hero-section/media`)
      .then(r => r.json())
      .then(d => {
        const active = (d.heroSectionMedia || []).find(m => m.activeStatus === 'active');
        setHeroMedia(active || null);
        setHeroSettled(true);
      })
      .catch(() => setHeroSettled(true));

    setHeroArmed(true);
  }, []);

  // Hold the hero entrance until the background photo is on screen.
  //
  // The animation is 820ms and starts the instant the CSS parses. The photo
  // cannot even begin downloading until the media request above answers, and
  // it is a multi-megabyte JPEG, so it routinely lands well after the motion
  // has finished. The headline was animating into an empty frame and sitting
  // still by the time there was anything to look at -- the animation ran
  // perfectly and nobody ever saw it.
  //
  // Every path ends with the hero shown: image loaded, image failed, no media
  // configured, or a slow connection that trips the cutoff. The hidden start
  // state is applied only once this component has mounted, so if the bundle
  // never runs the headline is simply visible and static.
  useEffect(() => {
    if (!heroSettled) return undefined;

    let cancelled = false;
    const show = () => { if (!cancelled) setHeroReady(true); };

    if (!heroMedia) { show(); return undefined; }

    const img = new Image();
    img.onload = show;
    img.onerror = show;
    img.src = `https://riymay.com/assets/${heroMedia.fileName}`;
    if (img.complete) show();

    // A stalled download must never leave the headline hidden.
    const cutoff = setTimeout(show, 2500);

    return () => { cancelled = true; clearTimeout(cutoff); };
  }, [heroSettled, heroMedia]);

  const heroStyle = heroMedia
    ? { backgroundImage: `url(https://riymay.com/assets/${heroMedia.fileName})` }
    : {};

  return (
    <div className="home-page">

      {/* ── HERO ── */}
      <section
        className={`home-hero${heroArmed ? ' is-armed' : ''}${heroReady ? ' is-ready' : ''}`}
        style={heroStyle}
        id="home-hero"
      >
        <div className="home-hero-overlay" />

        <div className="home-hero-stage">
          <h1 className="home-hero-title">
            <span className="home-hero-line"><span>Wear the mask.</span></span>
            <span className="home-hero-line"><span>Own the silence.</span></span>
          </h1>
          <p className="home-hero-lede">
            Premium materials, unisex silhouettes, precision finishing. Built to outlast seasons.
          </p>
          <Link to="/newproduct" className="home-hero-cta">Explore the collection</Link>
        </div>

        <div className="home-hero-scroll">
          <div className="home-hero-scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ── LATEST DROP ── */}
      {shelves.length > 0 && (
        <section className="home-latest">
          <div className="home-section-label">Latest drop</div>
          {shelves.map(shelf => (
            <div key={shelf._id} className="home-shelf">
              <h2 className="home-shelf-name">{shelf.name}</h2>
              <div className="home-shelf-grid">
                {shelf.products.map(product => (
                  <div
                    key={product._id}
                    className="home-product-card"
                    onClick={() => navigate(`/product/${product._id}`)}
                  >
                    {product.images && product.images[0] ? (
                      <img
                        src={`https://riymay.com/assets/${product.images[0].url}`}
                        alt={product.name}
                        className="home-product-img"
                      />
                    ) : (
                      <div className="home-product-img-placeholder" />
                    )}
                    <div className="home-product-info">
                      <span className="home-product-name">{product.name}</span>
                      <span className="home-product-price">₹{product.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ── COLLECTIONS ── */}
      {banners.length > 0 && (
        <section className="home-collections">
          <div className="home-section-label">Collections</div>
          {banners.map(banner => (
            <Link key={banner._id} to={banner.link || '/products-collection'} className="home-collection-banner">
              {banner.imageUrl && (
                <img
                  src={`https://riymay.com/assets/${banner.imageUrl}`}
                  alt={banner.title}
                  className="home-collection-bg"
                />
              )}
              <div className="home-collection-overlay" />
              <div className="home-collection-content">
                <span className="home-collection-title">{banner.title}</span>
                <span className="home-collection-cta">Explore →</span>
              </div>
            </Link>
          ))}
        </section>
      )}

    </div>
  );
};

export default Home;
